import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Button, Dropdown, theme } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  ApartmentOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  GiftOutlined,
  ExperimentOutlined,
  PictureOutlined,
  MessageOutlined,
  TrophyOutlined,
  StarOutlined,
  CommentOutlined,
} from '@ant-design/icons';
import { useAdminStore } from '@/stores/useAdminStore';

const { Header, Sider, Content } = Layout;

interface MenuItemDef {
  key: string;
  icon: React.ReactNode;
  label: string;
  permission?: string;
  children?: MenuItemDef[];
}

// 菜单按模块划分
const allMenuItems: MenuItemDef[] = [
  // 1. 概览模块
  { key: '/', icon: <DashboardOutlined />, label: '仪表盘' },

  // 2. 用户运营模块
  {
    key: '/users',
    icon: <UserOutlined />,
    label: '用户运营',
    children: [
      { key: '/users', icon: <UserOutlined />, label: '用户管理', permission: 'users:view' },
      { key: '/user-levels', icon: <TrophyOutlined />, label: '等级管理', permission: 'system:view' },
      { key: '/user-benefits', icon: <StarOutlined />, label: '权益管理', permission: 'system:view' },
    ],
  },

  // 3. 内容管理模块
  {
    key: '/content',
    icon: <PictureOutlined />,
    label: '内容管理',
    children: [
      { key: '/images', icon: <PictureOutlined />, label: '图片管理', permission: 'users:view' },
      { key: '/scenes', icon: <ApartmentOutlined />, label: '场景管理', permission: 'scenes:view' },
      { key: '/prompt-services', icon: <MessageOutlined />, label: '提示词服务', permission: 'system:view' },
    ],
  },

  // 4. 交易财务模块
  {
    key: '/finance',
    icon: <DollarOutlined />,
    label: '交易财务',
    children: [
      { key: '/orders', icon: <ShoppingCartOutlined />, label: '订单管理', permission: 'orders:view' },
      { key: '/credits', icon: <DollarOutlined />, label: '积分管理', permission: 'credits:view' },
    ],
  },

  // 5. 营销推广模块
  {
    key: '/marketing',
    icon: <GiftOutlined />,
    label: '营销推广',
    children: [
      { key: '/invites', icon: <GiftOutlined />, label: '邀请码管理' },
      { key: '/beta-invites', icon: <ExperimentOutlined />, label: '内测邀请码' },
    ],
  },

  // 6. 用户反馈模块
  { key: '/feedback', icon: <CommentOutlined />, label: '用户反馈', permission: 'system:view' },

  // 7. 系统设置模块
  { key: '/system', icon: <SettingOutlined />, label: '系统设置', permission: 'system:view' },
];

export default function AdminLayout() {
  const [collapsed, setCollapsed] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, logout, hasPermission } = useAdminStore();
  const { token: themeToken } = theme.useToken();

  // 递归处理菜单项，过滤权限并转换格式
  const processMenuItems = React.useCallback((items: MenuItemDef[]): any[] => {
    return items
      .filter(item => !item.permission || hasPermission(item.permission))
      .map(({ permission, children, ...item }) => {
        const menuItem: any = { ...item };
        if (children && children.length > 0) {
          menuItem.children = processMenuItems(children);
          // 如果子菜单都被过滤掉了，不显示父菜单
          if (menuItem.children.length === 0) {
            return null;
          }
        }
        return menuItem;
      })
      .filter(Boolean);
  }, [hasPermission]);

  const menuItems = React.useMemo(() => {
    return processMenuItems(allMenuItems);
  }, [processMenuItems]);

  const handleMenuClick = (info: { key: string }) => {
    navigate(info.key);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 计算当前选中的菜单项和展开的子菜单
  const { selectedKeys, openKeys } = React.useMemo(() => {
    const path = location.pathname;
    // 精确匹配当前路径
    const selected = [path];
    // 查找父菜单key用于展开
    const open: string[] = [];

    const findParent = (items: MenuItemDef[], parentKey?: string) => {
      for (const item of items) {
        if (item.children) {
          const childMatch = item.children.find(child => child.key === path);
          if (childMatch) {
            open.push(item.key);
            return;
          }
          findParent(item.children, item.key);
        }
      }
    };

    findParent(allMenuItems);
    return { selectedKeys: selected, openKeys: open };
  }, [location.pathname]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} theme="dark">
        <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: collapsed ? 14 : 18, fontWeight: 700, gap: 12 }}>
          <img src="/logo.png" alt="Logo" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover' }} />
          {!collapsed && <span>管理后台</span>}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={collapsed ? [] : openKeys}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header style={{ padding: '0 24px', background: themeToken.colorBgContainer, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0' }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />
          <Dropdown
            menu={{
              items: [
                { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true },
              ],
              onClick: ({ key }) => { if (key === 'logout') handleLogout(); },
            }}
          >
            <Button type="text" icon={<UserOutlined />}>
              {admin?.displayName || admin?.username || '管理员'}
            </Button>
          </Dropdown>
        </Header>
        <Content style={{ margin: 24, padding: 24, background: themeToken.colorBgContainer, borderRadius: 8, minHeight: 280 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
